import L from 'leaflet'
import { api } from '@/global/services/api'
import { isArray, isFunction } from '@/global/utils/utils'
import { useTranslate } from '@/global/composables/useTranslate'
import { toReportedAtLabel } from '@/modules/road-maintenance-module/winter-service-v2/irregularities/composables/irregularityData'
import { WINTER_SERVICE_MODULE } from '@/modules/road-maintenance-module/winter-service-v2/common/config/constants'
import { IRREGULARITIES_ENDPOINT } from '@/modules/road-maintenance-module/winter-service-v2/irregularities/config/constants'
import './useIrregularityMarkers.scss'

/**
 * The reports drivers send in, as signs on a map.
 *
 * Two screens show the same reports: the irregularities listing, where they are the
 * whole subject, and a work order's map, where they sit alongside the route that was
 * driven. They are the same thing seen from two places, so they are drawn by one piece
 * of code - the sign, what the popup says, and the photographs it fetches when a sign is
 * clicked. Written twice they would drift, and a report would start meaning two
 * different things depending on which screen it was opened from.
 *
 * What differs between the two callers is only what the popup's rows are called, which
 * is why the namespace is the one thing asked for: a work order's map says "Vrijeme" and
 * "Ulica" where the listing says "Datum prijave" and "Adresa".
 */

const MARKER_SIZE = 40
const MARKER_ICON_KEY = 'wsIrregularity'
const POPUP_MAX_WIDTH = 260
// A popup narrower than its photographs would have leaflet size it to the text and then
// grow it once they arrive.
const POPUP_MIN_WIDTH = 232
const POPUP_AUTO_PAN_PADDING = [16, 16]
// On a photo's box while the file is still on its way - see `trackPhotoLoading`.
const PHOTO_LOADING_CLASS = 'ws-irregularity-popup__photo--loading'
// On a photo that has arrived and can therefore be opened - see `trackPhotoPreview`.
const PHOTO_PREVIEW_CLASS = 'ws-irregularity-popup__photo--openable'

/**
 * What the details endpoint answered for a report, kept for the life of the page.
 *
 * Module level rather than per caller: the markers are rebuilt whenever the marked rows
 * change, and the same report opened from the listing and from a work order is the same
 * report. Without this its photographs would be fetched again on every redraw.
 */
const photoUrlsById = new Map()

/**
 * An amber warning sign on a dark disc, drawn inline rather than from an asset - there is
 * no warning icon in /img/icons and this keeps the marker self-contained. The disc has no
 * tail, so it is centred on the coordinate rather than standing above it.
 */
const warningIcon = L.divIcon({
  className: 'ws-irregularity-marker',
  iconSize: [MARKER_SIZE, MARKER_SIZE],
  iconAnchor: [MARKER_SIZE / 2, MARKER_SIZE / 2],
  html: `
    <svg width="${MARKER_SIZE}" height="${MARKER_SIZE}" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="20" fill="#0B0B0B"/>
      <!-- Stroked in its own colour so the corners round off the way the sign does. -->
      <path
        d="M20 11 L29 26.5 L11 26.5 Z"
        fill="#FACC15"
        stroke="#FACC15"
        stroke-width="3"
        stroke-linejoin="round"
      />
      <rect x="18.8" y="16" width="2.4" height="6" rx="1.2" fill="#0B0B0B"/>
      <circle cx="20" cy="24.1" r="1.3" fill="#0B0B0B"/>
    </svg>
  `
})

/**
 * Markup handed to leaflet is markup, so anything coming off a record has to be
 * neutralised before it lands in it. Exported because the maps build tooltips of their
 * own out of the same records.
 */
export function escapeHtml (value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Where a report was made. The listing reports it under `geolocation`; the details screen
 * flattens the pair onto the record itself (see `toIrregularityForm`), so both are read.
 */
export function getIrregularityCoordinates (irregularity) {
  const geolocation = irregularity?.geolocation || {}
  const latitude = irregularity?.latitude ?? geolocation.latitude
  const longitude = irregularity?.longitude ?? geolocation.longitude

  if (latitude == null || longitude == null) {
    return null
  }

  return [Number(latitude), Number(longitude)]
}

function popupRow (label, value) {
  if (value === null || value === undefined || value === '') {
    return ''
  }

  return `
    <div class="ws-irregularity-popup__row">
      <span class="ws-irregularity-popup__label">${escapeHtml(label)}</span>
      <span class="ws-irregularity-popup__value">${escapeHtml(value)}</span>
    </div>
  `
}

/**
 * The photographs' half of the popup.
 *
 * A listing carries no files, but it does say how many there are - so the boxes go up the
 * moment the popup opens, sweeping, and only what goes inside them is waited for. Without
 * that count the popup would open as text alone and grow a moment later, which is the
 * jump the fixed box heights are there to prevent.
 *
 * `photoUrls` is the answer from the details endpoint: a list once it has arrived, and
 * nothing at all while it has not. An empty list is an answer too - a report with no
 * photographs - and gets no boxes.
 */
function photosMarkup (irregularity, photoUrls) {
  const urls = isArray(photoUrls) ? photoUrls : null
  const count = urls ? urls.length : Number(irregularity?.photo_count) || 0

  if (!count) {
    return ''
  }

  // A report carries at most two, and both are worth seeing: the driver usually shoots
  // one wide and one close up. The count is said in the class rather than left to the
  // grid to work out, so the layout cannot depend on how wide the popup came out.
  const boxes = urls
    ? urls.map((url, index) => `<img class="ws-irregularity-popup__photo ${PHOTO_LOADING_CLASS} ${PHOTO_PREVIEW_CLASS}" data-photo-index="${index}" src="${escapeHtml(url)}" alt="" />`)
    : Array.from({ length: count }, () => `<div class="ws-irregularity-popup__photo ${PHOTO_LOADING_CLASS}"></div>`)

  return `<div class="ws-irregularity-popup__photos ws-irregularity-popup__photos--${count > 1 ? 'pair' : 'single'}">${boxes.join('')}</div>`
}

/**
 * The photos the driver's device uploaded with the report. Only the details endpoint
 * carries them, so they are asked for once a sign is actually opened rather than for every
 * report on the map.
 *
 * Answers null rather than an empty list when the ask itself failed: a report with no
 * photographs and a report whose photographs could not be reached are not the same thing,
 * and only the first is worth remembering.
 */
async function fetchPhotoUrls (irregularityId) {
  try {
    const { data } = await api(WINTER_SERVICE_MODULE).get(`${IRREGULARITIES_ENDPOINT}/${irregularityId}`)

    return (isArray(data?.photos) ? data.photos : [])
      .map(photo => photo?.url)
      .filter(Boolean)
  }
  catch (exception) {
    console.error(exception)

    return null
  }
}

/**
 * Sweeps a photo's box until the browser has the file, and stops when it does.
 *
 * The box is already the right size - see the stylesheet - so this only says that
 * something is on its way. Leaflet writes the popup's markup afresh every time it opens,
 * so the elements are new each time and so are these listeners.
 */
function trackPhotoLoading (popup) {
  const element = popup?.getElement()

  if (!element) {
    return
  }

  // Only the pictures: a box still waiting for its url is a plain div, and it is meant to
  // go on sweeping until one arrives.
  element.querySelectorAll(`img.${PHOTO_LOADING_CLASS}`).forEach(image => {
    const settle = () => image.classList.remove(PHOTO_LOADING_CLASS)

    // Cached, so it is already here and no event is coming.
    if (image.complete) {
      settle()

      return
    }

    // On error as well: a photograph that never arrives must not leave its box sweeping
    // for ever.
    image.addEventListener('load', settle, { once: true })
    image.addEventListener('error', settle, { once: true })
  })
}

export function useIrregularityMarkers (options = {}) {
  const { translationNamespace, onPhotoPreview } = options

  // Asked for here rather than at the top of the file. `useTranslate` reaches for the
  // component that is being set up, and in a plain module the top level runs when the
  // module is first imported - long before any component exists. A `<script setup>`
  // block gets away with it because all of it is the setup function; this is not one.
  const { translate } = useTranslate()

  const label = (key) => translate(`${translationNamespace}.${key}`)

  /**
   * Makes a photograph in the popup open the full-size preview.
   *
   * A popup is markup handed to leaflet rather than a component, so the click cannot be
   * bound in a template - it is put on the elements once they exist, the same way the
   * sweep is. A caller that wants nothing to happen simply passes no handler, and the
   * photographs stay what they were: something to look at in place.
   */
  const trackPhotoPreview = (popup, irregularity, photoUrls) => {
    if (!isFunction(onPhotoPreview) || !isArray(photoUrls) || !photoUrls.length) {
      return
    }

    const element = popup?.getElement()

    if (!element) {
      return
    }

    element.querySelectorAll(`img.${PHOTO_PREVIEW_CLASS}`).forEach(image => {
      image.addEventListener('click', () => {
        onPhotoPreview({
          images: photoUrls,
          index: Number(image.dataset.photoIndex) || 0,
          title: irregularity?.type?.description || ''
        })
      })
    })
  }

  /**
   * What a marked report says, with its photographs where they will be.
   */
  const buildPopupContent = (irregularity, photoUrls) => {
    const rows = [
      popupRow(label('irregularity_type'), irregularity?.type?.description),
      popupRow(label('reported_at'), toReportedAtLabel(irregularity)),
      popupRow(label('address'), irregularity?.geolocation?.address ?? irregularity?.address)
    ].join('')

    return `<div class="ws-irregularity-popup">${rows}${photosMarkup(irregularity, photoUrls)}</div>`
  }

  /**
   * Gives a report's sign its popup, and opens it the first time.
   *
   * `bindPopup` gives the marker a click handler of leaflet's own, which toggles the
   * popup. That handler does not run for the click that added it, so the first open has
   * to be done here - but on every click after that it does, and it would fight an
   * `openPopup` of ours: this opens the popup, leaflet's handler sees an open one and
   * closes it again, and a report could be opened once and never again. Binding once and
   * opening only then leaves the toggling to leaflet, which is what it is for.
   */
  const openPopup = async (event, irregularity) => {
    const layer = event?.target

    if (!layer) {
      return
    }

    const irregularityId = irregularity?.id
    const knownPhotos = photoUrlsById.get(irregularityId)

    if (!layer.getPopup()) {
      layer.bindPopup(buildPopupContent(irregularity, knownPhotos), {
        maxWidth: POPUP_MAX_WIDTH,
        minWidth: POPUP_MIN_WIDTH,
        autoPanPadding: POPUP_AUTO_PAN_PADDING,
        className: 'ws-irregularity-popup-wrapper',
        closeButton: true
      })

      // Every open, not only this one: leaflet rewrites the markup each time, so the
      // boxes it hands back are new elements with the loading class still on them.
      layer.on('popupopen', ({ popup }) => {
        trackPhotoLoading(popup)
        trackPhotoPreview(popup, irregularity, photoUrlsById.get(irregularityId))
      })
      layer.openPopup()
    }

    if (knownPhotos) {
      layer.setPopupContent(buildPopupContent(irregularity, knownPhotos))
      trackPhotoLoading(layer.getPopup())
      trackPhotoPreview(layer.getPopup(), irregularity, knownPhotos)

      return
    }

    const photoUrls = await fetchPhotoUrls(irregularityId)

    if (photoUrls) {
      photoUrlsById.set(irregularityId, photoUrls)
    }

    // The user may well have closed it again while the photos were on their way.
    if (!layer.isPopupOpen()) {
      return
    }

    // Set either way, so the waiting boxes always resolve - into the photographs, or into
    // nothing at all when there are none or the ask failed. Left as they were they would
    // sweep for ever. A failed ask is not remembered, so opening the report again retries.
    layer.setPopupContent(buildPopupContent(irregularity, photoUrls || []))
    // Setting the content is not an open, so the popup fires nothing - the boxes that
    // just appeared have to be picked up here.
    trackPhotoLoading(layer.getPopup())
    trackPhotoPreview(layer.getPopup(), irregularity, photoUrls)
  }

  /**
   * Draws the given reports on the given map, replacing whatever was drawn before.
   *
   * Through `generateMarkers` rather than as part of the polyline group, for two reasons
   * either of which would be enough on its own. The polyline group draws its markers only
   * when there is a polyline to draw them alongside, and a report is a place rather than a
   * journey - most carry no route, so nothing appeared at all. And only this path binds a
   * click handler, which is what opens the report.
   *
   * `fitMarkers` is the caller's to decide: a map that already has a route on it is
   * fitted to that, while a map of reports alone has nothing else to fit to.
   */
  const renderMarkers = (mapBase, irregularities, { fitMarkers = true } = {}) => {
    if (!mapBase) {
      return
    }

    mapBase.removeMarkers()

    const markers = (isArray(irregularities) ? irregularities : [])
      .reduce((collected, irregularity) => {
        const coordinates = getIrregularityCoordinates(irregularity)

        if (!coordinates) {
          return collected
        }

        collected.push({
          id: `ws-irregularity-${irregularity.id}`,
          lat: coordinates[0],
          lon: coordinates[1],
          icon: MARKER_ICON_KEY,
          // Every sign is worth seeing where it is - two reports at the same spot must
          // not silently drop one of them.
          alwaysVisible: true,
          click: (event) => openPopup(event, irregularity)
        })

        return collected
      }, [])

    if (!markers.length) {
      return
    }

    mapBase.generateMarkers({
      markers,
      icons: {
        [MARKER_ICON_KEY]: warningIcon
      },
      // Clustering would hide the signs behind a count, which is the one thing the map is
      // being asked to show.
      useClustering: false,
      fitMarkers
    })
  }

  return {
    renderMarkers
  }
}
